import { AgGridReact } from '@ag-grid-community/react';
import { useAgGrid } from '@tassat/ag-grid';
import { AgGridFilterType, FilterOption } from '@tassat/common-definitions';
import { AccountRecord } from '@tassat/fred-definitions';
import React from 'react';

import history from 'Lib/browserHistory';
import { AG_GRID_SMALL_HEADER_HEIGHT } from 'Lib/constant';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAllMappedAccountsByFirmId, getAllMappedWalletsByFirmId } from 'Selectors/accounts';
import LastTransactionDateCellRenderer from 'SharedComponents/cellRenderComponent/lastTransactionDateCellRenderer';
import PrimaryWalletCellRenderer from 'SharedComponents/cellRenderComponent/primaryWalletCellRenderer';
import WalletStatusCellRenderer from 'SharedComponents/cellRenderComponent/walletStatusCellRenderer';

import { columnDefs } from './firmAccountsAgGridOptions';

import type { FirmAccountsAgGridProps } from '.';

import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';
import 'SharedComponents/agGrid/styles/agGridOverrides.css';

const closedWalletFilter: FilterOption = {
    title: 'Closed Accounts',
    colId: 'IsClosed',
    type: AgGridFilterType.Equal,
    filter: 'false'
};

export function FirmAccountsAgGrid(props: FirmAccountsAgGridProps) {
    const {
        firmId,
        showClosedAccounts
    } = props;

    const accounts = useAppSelector((state) => getAllMappedAccountsByFirmId(state, firmId));
    const wallets = useAppSelector((state) => getAllMappedWalletsByFirmId(state, firmId));

    const firmAccountSelected = (params: { data: AccountRecord }) => {
        if (window.getSelection()
            .toString().length > 0) return;

        const account = params.data;
        history.push(`/admin/firms/${account.FirmId}/bdaccounts/${account.AccountId}`);
    };

    const applyClosedFilter = (params, enable = false) => {
        const model = {
            filterType: 'text',
            type: 'equals',
            filter: enable ? 'true' : 'false'
        };
        params.api.setColumnFilterModel(closedWalletFilter.colId, model);
        params.api.onFilterChanged();
    };

    const [ gridApi, onGridReady ] = useAgGrid(applyClosedFilter);

    React.useEffect(() => {
        if (gridApi) {
            applyClosedFilter(gridApi, showClosedAccounts);
        }
    }, [ showClosedAccounts, gridApi, accounts ]);

    return (
        <div
            className='ag-theme-material'
            id='firmAccountsGrid'
            style={{ height: '100%' }}
        >
            <AgGridReact
                animateRows={false}
                columnDefs={columnDefs}
                columnMenu='legacy'
                components={{
                    lastTransactionDateCellRenderer: LastTransactionDateCellRenderer,
                    walletStatusCellRenderer: WalletStatusCellRenderer,
                    primaryWalletCellRenderer: PrimaryWalletCellRenderer
                }}
                context={{ wallets: structuredClone(wallets) }}
                defaultColDef={{
                    resizable: true,
                    filter: true,
                    sortable: true
                }}
                enableCellTextSelection={true}
                getRowId={({ data }) => data?.AccountId}
                headerHeight={AG_GRID_SMALL_HEADER_HEIGHT}
                onGridReady={onGridReady}
                onRowClicked={firmAccountSelected}
                rowClassRules={{ 'lockedRow': (params: { data: AccountRecord }) => params.data.IsLocked }}
                rowData={accounts}
                suppressCellFocus={true}
                suppressContextMenu={true}
            />
        </div>
    );
}